import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertCircle, ArrowLeft, Copy, Download } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ErrorPageProps {
  error?: Error;
  resetErrorBoundary?: () => void;
  errorType?: 'network' | 'server' | 'client' | 'unknown';
}

export default function ErrorPage({ error, resetErrorBoundary }: ErrorPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [errorLog, setErrorLog] = useState<string[]>([]);

  // Get error information from location state if available
  const errorInfo = useMemo(() => {
    return (
      location.state?.error ||
      error || {
        message: 'An unexpected error occurred',
        stack: 'No stack trace available',
      }
    );
  }, [location.state?.error, error]);

  // Determine error type
  const errorType = useMemo(() => {
    if (location.state?.errorType) return location.state.errorType;

    const errorMessage = errorInfo.message.toLowerCase();
    if (
      errorMessage.includes('network') ||
      errorMessage.includes('fetch') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('offline')
    ) {
      return 'network';
    }

    if (
      errorMessage.includes('500') ||
      errorMessage.includes('server') ||
      errorMessage.includes('unavailable')
    ) {
      return 'server';
    }

    if (
      errorMessage.includes('permission') ||
      errorMessage.includes('unauthorized') ||
      errorMessage.includes('forbidden')
    ) {
      return 'client';
    }

    return 'unknown';
  }, [errorInfo, location.state?.errorType]);

  useEffect(() => {
    // Add timestamp to error log
    const timestamp = new Date().toISOString();
    const logEntry = `[${timestamp}] ${errorInfo.message}\n${errorInfo.stack || ''}`;
    setErrorLog((prev) => [...prev, logEntry]);

    // Log to console for debugging
    console.error('Error caught by ErrorPage:', errorInfo);
  }, [errorInfo]);

  const handleGoBack = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    }
    navigate('/');
  };

  const handleCopyLog = () => {
    const logText = errorLog.join('\n\n');
    navigator.clipboard.writeText(logText);
    toast({
      title: 'Error log copied',
      description: 'The error log has been copied to your clipboard',
    });
  };

  const handleDownloadLog = () => {
    const logText = errorLog.join('\n\n');
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-log-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: 'Error log downloaded',
      description: 'The error log has been downloaded as a text file',
    });
  };

  const handleClearLog = () => {
    setErrorLog([]);
    toast({
      title: 'Error log cleared',
      description: 'The error log has been cleared',
    });
  };

  return (
    <div className="flex min-h-screen flex-col bg-[#111]">
      <Card className="mx-auto mt-12 w-full max-w-4xl border-[#2E3835] bg-card2 text-white">
        <CardHeader className="border-b border-[#2E3835]">
          <div className="flex items-center">
            <AlertCircle className="mr-3 h-8 w-8 text-red-500" />
            <div>
              <CardTitle className="text-2xl text-white">Error Occurred</CardTitle>
              <CardDescription className="text-gray-400">
                {errorType === 'network' && 'Network error - Please check your connection'}
                {errorType === 'server' && 'Server error - Our systems are experiencing issues'}
                {errorType === 'client' && 'Client error - There was a problem with your request'}
                {errorType === 'unknown' && 'An unexpected error has occurred'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-6">
          <div className="mb-6">
            <h3 className="mb-2 text-lg font-semibold">Error Details</h3>
            <p className="mb-2 text-white">{errorInfo.message}</p>
            {errorInfo.stack && (
              <pre className="overflow-x-auto rounded-md bg-[#0F1613] p-3 text-sm text-gray-300">
                {errorInfo.stack}
              </pre>
            )}

            {/* Troubleshooting suggestions based on error type */}
            <div className="mt-4 rounded-md border border-[#2E3835] bg-card2 p-4">
              <h4 className="text-md mb-2 font-semibold">Troubleshooting Suggestions</h4>
              {errorType === 'network' && (
                <ul className="list-disc space-y-1 pl-5 text-gray-300">
                  <li>Check your internet connection</li>
                  <li>Try refreshing the page</li>
                  <li>Clear your browser cache and cookies</li>
                  <li>Try again in a few minutes</li>
                </ul>
              )}
              {errorType === 'server' && (
                <ul className="list-disc space-y-1 pl-5 text-gray-300">
                  <li>Our team has been notified of this issue</li>
                  <li>Please try again later</li>
                  <li>Check system status for any ongoing maintenance</li>
                </ul>
              )}
              {errorType === 'client' && (
                <ul className="list-disc space-y-1 pl-5 text-gray-300">
                  <li>Try logging out and logging back in</li>
                  <li>Check if you have the necessary permissions</li>
                  <li>Clear your browser cache and cookies</li>
                </ul>
              )}
              {errorType === 'unknown' && (
                <ul className="list-disc space-y-1 pl-5 text-gray-300">
                  <li>Try refreshing the page</li>
                  <li>Try logging out and logging back in</li>
                  <li>Clear your browser cache and cookies</li>
                </ul>
              )}
            </div>
          </div>

          <div>
            <h3 className="mb-2 text-lg font-semibold">Error Log</h3>
            <ScrollArea className="h-[300px] rounded-md border border-[#2E3835]">
              <div className="bg-[#0F1613] p-4">
                {errorLog.length > 0 ? (
                  errorLog.map((log, index) => (
                    <pre key={index} className="mb-4 whitespace-pre-wrap text-sm text-gray-300">
                      {log}
                    </pre>
                  ))
                ) : (
                  <p className="italic text-gray-400">No errors logged</p>
                )}
              </div>
            </ScrollArea>
          </div>
        </CardContent>

        <CardFooter className="flex justify-between border-t border-[#2E3835] pt-4">
          <Button variant="destructive" onClick={handleClearLog} disabled={errorLog.length === 0}>
            Clear Log
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCopyLog}
              disabled={errorLog.length === 0}
              className="border-[#2E3835] hover:bg-[#2E3835]"
            >
              <Copy className="mr-2 h-4 w-4" />
              Copy Log
            </Button>
            <Button
              onClick={handleDownloadLog}
              disabled={errorLog.length === 0}
              className="bg-[#B7D257] text-black hover:bg-[#D1E47F]"
            >
              <Download className="mr-2 h-4 w-4" />
              Download Log
            </Button>
          </div>
        </CardFooter>
      </Card>
      <div className="mb-12 flex items-center justify-center px-0">
        <Button
          variant="outline"
          onClick={handleGoBack}
          className="mt-12 w-full max-w-4xl p-6 text-[#B7D257] hover:bg-card2 hover:text-[#D1E47F]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
      </div>
    </div>
  );
}
